import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule, NgForm, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams,
  REMOVED,
  TestSetupModule
} from '@nrwl/test-setup';
import { CommonLibModule, CommonRegEx } from '@nrwl/common-lib';
import {
  Y1731ConfigFieldMapping as ConfigFieldMapping,
  DEFAULT_FLOWS,
  MEG_LEVEL
} from '../../models/models';
import { Y1731SetupService } from '../../y1731-setup.service';
import { CommonMaterialModule } from '@nrwl/common-material';
import { isNil, cloneDeep } from 'lodash';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Y1731PerformanceMonitorComponent } from '../y1731-performance-monitor/y1731-performance-monitor.component';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'nrwl-y1731-flows',
  standalone: true,
  imports: [CommonModule, TestSetupModule, CommonMaterialModule, FormsModule, CommonLibModule],
  templateUrl: './y1731-flows.component.html',
  styleUrls: ['./y1731-flows.component.scss']
})
export class Y1731FlowsComponent implements OnInit, OnDestroy {
  @Input() tab: BaseSetupTab;
  @Input() setupCtrl: any;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  @Input() restoreValues?: Function;
  @Input() restoreAgentBasedConfig?: Function;
  @Input() previousOutput: outputParams;
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;
  readonly unsubscribe: Subject<void> = new Subject();
  tabsubscription: Subscription;
  defaultFlowObj: any;
  flowDataSource: MatTableDataSource<any>;
  megLevelDropdown = MEG_LEVEL;
  defaultFlow = DEFAULT_FLOWS;
  macRegex = CommonRegEx.MAC;
  displayedFlowsColumns = ['flowId', 'destinationMac', 'megLevel', 'megId', 'add'];
  flows = [];
  codecValue: string;
  config: any;
  parentConfig: any;
  flowsConfig: any;
  outputs: Array<string>;
  flowIndex = 1;
  flowIdExist: boolean;
  availableFlowNames: string[] = [];
  flowsRow = {
    flowId: null,
    destinationMac: null,
    megLevel: null,
    megId: null,
    flowIdExist: false,
    attributes: {
      megLevel: null,
      standard: null
    },
    pm: {
      slmPeriod: null,
      slmDataTlvSize: null,
      dmmPeriod: null,
      dmmDataTlvSize: null
    },
    ccm: {
      ccmPeriod: null
    }
  };
  maxFlowsReached: boolean;

  constructor(
    public setupService: Y1731SetupService,
    private dialog: MatDialog,
    private cdRef: ChangeDetectorRef
  ) {
    this.flowDataSource = new MatTableDataSource<any>([]);
  }
  ngOnInit(): void {
    this.tab.form = this.form;
    this.restoreFormValues();
    if (this.setupService.tabDataChangeEvents) {
      this.tabsubscription = this.setupService.tabDataChangeEvents.subscribe(event => {
        if (event === 1) {
          this.restoreFormValues();
        }
      });
    }
  }

  restoreFormValues(): void {
    this.config = this.tab?.config;
    this.defaultFlowObj = this.restoreFieldValues(ConfigFieldMapping.FLOWS);
    this.parentConfig = this.tab?.['parent']['output'];
    this.outputs = this.config?.def?.outputs;
    const defaultFlowsRow = this.flowsRow;
    this.flowsConfig = cloneDeep(this.restoreFieldValues(ConfigFieldMapping.FLOWS));
    if (this.flowsConfig.length > 0) {
      this.flowDataSource.data = [];
      for (const element of this.flowsConfig) {
        const obj = {};
        obj['flowId'] = element['flow-id'];
        obj['destinationMac'] = element['remote-mac-addr'];
        obj['megLevel'] = element['attributes']['meg-level'];
        obj['megId'] = element[ConfigFieldMapping.MEG_ID];
        obj['attributes'] = {};
        obj['attributes']['megLevel'] = element['attributes']['meg-level'];
        obj['attributes']['standard'] = element[ConfigFieldMapping.ATTRIBUTES]?.['standard'];
        if (element[ConfigFieldMapping.CCM]?.['ccm-period'] !== 'off') {
          obj['attributes']['megIdFormat'] =
            element[ConfigFieldMapping.ATTRIBUTES]?.['meg-id-format'];
          obj['attributes']['megId'] = element[ConfigFieldMapping.ATTRIBUTES]?.['meg-id'];
        }
        if (element[ConfigFieldMapping.ATTRIBUTES]?.['standard'] !== 'ITU') {
          obj['attributes']['mdNameFormat'] =
            element[ConfigFieldMapping.ATTRIBUTES]?.['md-name-format'];
          obj['attributes']['mdName'] = element[ConfigFieldMapping.ATTRIBUTES]?.['md-name'];
        }
        obj['pm'] = {};
        obj['pm']['slmPeriod'] = element[ConfigFieldMapping.PM]?.['slm-period'];
        obj['pm']['slmDataTlvSize'] = element[ConfigFieldMapping.PM]?.['slm-data-tlv-size'];
        obj['pm']['dmmPeriod'] = element[ConfigFieldMapping.PM]?.['dmm-period'];
        obj['pm']['dmmDataTlvSize'] = element[ConfigFieldMapping.PM]?.['dmm-data-tlv-size'];
        obj['ccm'] = {};
        obj['ccm']['ccmPeriod'] = element[ConfigFieldMapping.CCM]?.['ccm-period'];
        this.flowDataSource.data.push(obj);
      }
    } else {
      this.flowsRow = cloneDeep(defaultFlowsRow);
      this.flowsRow.flowId = this.flowsConfig[ConfigFieldMapping.FLOW_ID];
      this.flowsRow.destinationMac = this.restoreFieldValues(ConfigFieldMapping.DESTINATION_MAC);
      this.flowsRow.megLevel = this.flowsConfig[ConfigFieldMapping.MEG_LEVEL];
      this.flowsRow.megId = this.flowsConfig[ConfigFieldMapping.MEG_ID];
      this.flowsRow.attributes.megLevel =
        this.flowsConfig[ConfigFieldMapping.ATTRIBUTES]?.['megLevel'] ||
        this.flowsConfig[ConfigFieldMapping.MEG_LEVEL];
      this.flowsRow.attributes.standard =
        this.flowsConfig[ConfigFieldMapping.ATTRIBUTES]?.['standard'];
      this.flowsRow.pm.slmPeriod = this.flowsConfig[ConfigFieldMapping.PM]?.['slmPeriod'];
      this.flowsRow.pm.slmDataTlvSize = this.flowsConfig[ConfigFieldMapping.PM]?.['slmDataTlvSize'];
      this.flowsRow.pm.dmmPeriod = this.flowsConfig[ConfigFieldMapping.PM]?.['dmmPeriod'];
      this.flowsRow.pm.dmmDataTlvSize = this.flowsConfig[ConfigFieldMapping.PM]?.['dmmDataTlvSize'];
      this.flowsRow.ccm.ccmPeriod = this.flowsConfig[ConfigFieldMapping.CCM]?.['ccmPeriod'];
      this.flowDataSource.data = [this.flowsRow];
    }
    this.flowDataSource = new MatTableDataSource<any>(this.flowDataSource.data);
    this.flowIndex = this.flowDataSource.data.length;
    this.formattingAllFlowsData();
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  addFlow(): void {
    this.availableFlowNames = [];
    if (this.flowDataSource?.data.length > 0) {
      this.flowDataSource.data.forEach((item: any) => this.availableFlowNames.push(item.flowId));
    } else {
      this.flowIndex = 0;
    }
    this.flowIndex++;
    const row = {
      flowId: 'Flow' + this.flowIndex,
      destinationMac: '',
      megLevel: 0,
      megId: '--',
      nameExist: false,
      attributes: {
        megLevel: 0,
        standard: 'ITU'
      },
      pm: {
        slmPeriod: 'off',
        slmDataTlvSize: 0,
        dmmPeriod: '1s',
        dmmDataTlvSize: 0
      },
      ccm: { ccmPeriod: 'off' }
    };
    if (this.availableFlowNames.includes(row.flowId)) {
      row.flowId = row.flowId + '-' + 1;
    }
    if (this.flowDataSource.data.length !== 100) {
      this.flowDataSource.data.push(row);
    }
    this.flowDataSource = new MatTableDataSource<any>(this.flowDataSource.data);
    this.flowIndex = this.flowDataSource.data.length;
    if (this.flowDataSource.data.length === 100) {
      this.maxFlowsReached = true;
    }
  }

  flowIdUpdated(index: number, element: any): void {
    const flowIds = [];
    this.flowDataSource.data.filter((item, i) => {
      if (i !== index) {
        flowIds.push(item.flowId);
      }
    });
    if (flowIds.includes(element.flowId)) {
      element.flowIdExist = true;
      this.onDataEmit(ConfigFieldMapping.FLOW_ID, null, false);
    } else {
      element.flowIdExist = false;
      const index = this.flowDataSource.data.findIndex(item => item.flowIdExist);
      if (index < 0) {
        this.onDataEmit(ConfigFieldMapping.FLOW_ID, element.flowId, true);
      }
    }
    this.flowDataSource = new MatTableDataSource<any>(this.flowDataSource.data);
    if (this.form.valid) {
      this.formattingAllFlowsData();
    }
  }

  onDestinationMacAddressChange(name: any, element: any): void {
    this.form.controls[name].setValidators([
      Validators.required,
      Validators.pattern(this.macRegex)
    ]);
    this.form.controls[name].updateValueAndValidity();
    if (this.form.valid) {
      this.onDataEmit(ConfigFieldMapping.DESTINATION_MAC, element.destinationMac, true);
      this.onDataEmit(ConfigFieldMapping.FLOW_ID, element.flowId, true);
      this.formattingAllFlowsData();
    }
  }

  onMegLevelChange(element: any): void {
    this.onDataEmit(ConfigFieldMapping.MEG_LEVEL, element.megLevel, true);
    if (this.form.valid) {
      this.formattingAllFlowsData();
    }
  }

  removeFlow(element: any, index: number) {
    this.flowDataSource.data = this.flowDataSource.data.filter(item => item !== element);
    this.flowDataSource = new MatTableDataSource<any>(this.flowDataSource.data);
    this.cdRef.detectChanges();
    if (this.flowDataSource?.data?.length === 0) {
      this.onDataEmit(ConfigFieldMapping.DESTINATION_MAC, element.destinationMac, false);
    }
    const flowIdIndex = this.flowDataSource.data.findIndex(item => item.flowIdExist);
    if (flowIdIndex < 0) {
      this.onDataEmit(ConfigFieldMapping.FLOW_ID, element.flowId, true);
    }
    if (this.flowDataSource.data.length === 100) {
      this.maxFlowsReached = true;
    } else {
      this.maxFlowsReached = false;
    }
    if (this.form.valid) {
      this.formattingAllFlowsData();
    }
  }

  cloneFlow(element: any) {
    const row = {
      flowId: element.flowId + '-Clone',
      destinationMac: element.destinationMac,
      megLevel: element.megLevel,
      megId: element.megId,
      flowIdExist: element.flowIdExist,
      attributes: {
        megLevel: element.attributes.megLevel,
        standard: element.attributes.standard
      },
      pm: {
        slmPeriod: element.pm.slmPeriod,
        slmDataTlvSize: element.pm.slmDataTlvSize,
        dmmPeriod: element.pm.dmmPeriod,
        dmmDataTlvSize: element.pm.dmmDataTlvSize
      },
      ccm: { ccmPeriod: element.ccm.ccmPeriod }
    };
    if (element?.ccm?.ccmPeriod !== 'off') {
      row.attributes['megIdFormat'] = element.attributes.megIdFormat;
      row.attributes['megId'] = element.attributes.megId;
    }
    if (element?.attributes?.standard !== 'off') {
      row.attributes['mdNameFormat'] = element.attributes.mdNameFormat;
      row.attributes['mdName'] = element.attributes.mdName;
    }
    this.flowDataSource.data.forEach((item: any) => {
      if (item.flowId === row.flowId) {
        row.flowIdExist = true;
        this.onDataEmit(ConfigFieldMapping.FLOW_ID, null, false);
      } else {
        row.flowIdExist = false;
      }
    });
    if (this.flowDataSource.data.length !== 100) {
      this.flowDataSource.data.push(row);
    }
    this.flowDataSource = new MatTableDataSource<any>(this.flowDataSource.data);
    this.flowIndex = this.flowDataSource.data.length;
    if (this.flowDataSource.data.length === 100) {
      this.maxFlowsReached = true;
    }
    if (this.form.valid) {
      this.formattingAllFlowsData();
    }
  }

  onClickEdit(element: any, index: number): void {
    const dialogRef = this.dialog.open(Y1731PerformanceMonitorComponent, {
      data: element,
      disableClose: true
    });
    dialogRef.beforeClosed().subscribe(_res => {
      if (_res) {
        element.attributes['megLevel'] = element?.megLevel;
        element.attributes['standard'] = _res?.standard;
        element.attributes['megIdFormat'] = _res?.megIdFormat;
        element.attributes['megId'] = _res?.megId || '';
        element.megId = _res?.megId;
        element.attributes['mdNameFormat'] = _res?.mdNameFormat;
        element.attributes['mdName'] = _res?.mdName || '';
        element.ccm['ccmPeriod'] = _res?.ccmPeriod;
        element.pm['slmPeriod'] = _res?.slmPeriod;
        element.pm['slmDataTlvSize'] = _res?.slmDataSize;
        element.pm['dmmPeriod'] = _res?.dmmPeriod;
        element.pm['dmmDataTlvSize'] = _res?.dmmDataSize;
      }
      this.formattingAllFlowsData();
    });
  }

  formattingAllFlowsData(): void {
    this.flows = [];
    if (this.form.valid) {
      this.flowDataSource.data.forEach((item: any) => {
        const obj = {};
        obj['flow-id'] = item.flowId;
        obj['remote-mac-addr'] = item.destinationMac;
        obj['attributes'] = {};
        obj['attributes']['meg-level'] = Number(item.megLevel);
        obj['attributes']['standard'] = item?.attributes?.standard;
        obj['pm'] = {};
        obj['pm']['slm-period'] = item.pm.slmPeriod;
        obj['pm']['slm-data-tlv-size'] = item.pm.slmDataTlvSize;
        obj['pm']['dmm-period'] = item.pm.dmmPeriod;
        obj['pm']['dmm-data-tlv-size'] = item.pm.dmmDataTlvSize;
        obj['ccm'] = {};
        obj['ccm']['ccm-period'] = item.ccm.ccmPeriod;
        if (item?.ccm?.ccmPeriod !== 'off') {
          obj['attributes']['meg-id-format'] = item?.attributes?.megIdFormat;
          obj['attributes']['meg-id'] = item?.attributes?.megId || null;
        }
        if (item?.attributes?.standard !== 'ITU') {
          obj['attributes']['md-name-format'] = item?.attributes?.mdNameFormat;
          obj['attributes']['md-name'] = item?.attributes?.mdName || null;
        }
        this.flows.push(obj);
      });
      this.onDataEmit(ConfigFieldMapping.FLOWS, this.flows, true);
    }
  }

  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.complete();
    this.unsubscribe.next();
    if (this.tabsubscription) {
      this.tabsubscription.unsubscribe();
    }
  }
}
